import React, {ReactElement} from 'react'
import {
  MessageHandlerContext,
  ViewerMode,
  ExternalViewerConfig,
} from './handler-structures'
import styles from './style.module.css'
import {
  isViewerManifestFresh,
  loadViewerManifest,
  peekViewerVersion,
} from '../../services/viewerManifest'

type ExternalViewerState = {
  /** Version resolved from the published manifest, once it has been read. */
  manifestVersion?: string
  /**
   * True while the tab holds off painting its iframe until the manifest re-read settles. Only ever
   * set at construction, and only for a tab whose version comes from the manifest.
   */
  awaitingManifest: boolean
}

/**
 * Longest a tab stays blank waiting for the manifest before painting with what it already has. The
 * re-read is normally a 304 answered in well under this; the bound only matters when the CDN hangs,
 * which must not leave a tab empty indefinitely.
 */
const MANIFEST_WAIT_MS = 3000

// PureComponent seems to avoid re-rendering
export class ExternalViewer
  extends React.PureComponent<ExternalViewerConfig, ExternalViewerState>
  implements ReactElement
{
  type: string | React.JSXElementConstructor<any>
  key: string

  private iframeRef: React.RefObject<HTMLIFrameElement>

  static contextType = MessageHandlerContext

  context!: React.ContextType<typeof MessageHandlerContext>

  // Declare not supported by jest & react-scripts
  // declare context: React.ContextType<typeof MessageHandlerContext>

  private mounted = false

  private manifestWaitTimer?: ReturnType<typeof setTimeout>

  constructor(props: ExternalViewerConfig) {
    super(props)

    this.type = 'ExternalViewer'
    this.key = props.objectInfo.id
    this.iframeRef = React.createRef()
    if (isViewerManifestFresh() || props.viewerURL || !props.controllerInfo?.viewerAppName) {
      // Synchronous read: the common case, since the viewer managers warm the manifest on their own
      // mount. Also taken by tabs whose URL does not come from the manifest at all.
      this.state = {
        manifestVersion: peekViewerVersion(props.controllerInfo?.viewerAppName),
        awaitingManifest: false,
      }
    } else {
      // The cache has aged out (or has not landed yet), so a synchronous read could only name the
      // version the re-read is about to replace — and once an iframe is painted with it, the guard in
      // componentDidMount keeps it there for the life of the tab. Wait out the re-read instead.
      this.state = {awaitingManifest: true}
    }
  }

  /**
   * Version this tab is currently pointed at, if any. `undefined` means nothing is on screen yet:
   * neither the manifest nor the restored config could name a version, so render shows the
   * "Viewer unavailable" panel instead of an iframe.
   */
  private renderedVersion(): string | undefined {
    return this.state.manifestVersion ?? this.props.controllerInfo?.viewerVersion
  }

  componentDidMount() {
    this.mounted = true
    this.context.registerViewer(this.key, this.iframeRef)

    // The published manifest is the authority on which version of a viewer is actually deployed.
    // The version carried on the config is only a fallback: it was resolved when the file was
    // opened, and a config restored from localStorage keeps that value forever — so without this a
    // tab reopened after a viewer release would point at a version that is no longer published.
    // Reconciled on *every* mount, not only when no version is known yet: the shared manifest cache
    // is time-bounded, so this is what re-reads it once it has aged out and lets a viewer published
    // mid-session be picked up without reloading the whole page.
    if (this.state.awaitingManifest) {
      this.manifestWaitTimer = setTimeout(this.adoptManifestVersion, MANIFEST_WAIT_MS)
    }
    void loadViewerManifest().then(() => {
      if (this.mounted) {
        this.adoptManifestVersion()
      }
    })
  }

  componentWillUnmount() {
    this.mounted = false
    clearTimeout(this.manifestWaitTimer)
    this.context.unregisterViewer(this.key)
  }

  /**
   * Apply whatever the manifest cache holds now: the fresh copy once the re-read has landed, or the
   * last known good one if it failed or is still in the air when {@link MANIFEST_WAIT_MS} runs out.
   * Runs from both of those, in either order, so it goes through a state updater rather than reading
   * `this.state` — a setState from the other one may not have been committed yet.
   */
  private adoptManifestVersion = () => {
    clearTimeout(this.manifestWaitTimer)
    const resolved = peekViewerVersion(this.props.controllerInfo?.viewerAppName)
    this.setState((state, props): ExternalViewerState | null => {
      if (state.awaitingManifest) {
        // No iframe yet, so nothing to lose — and `undefined` still falls back to the config version.
        return {manifestVersion: resolved, awaitingManifest: false}
      }
      // Adopt a newly discovered version ONLY when this tab has nothing on screen to lose. Changing
      // `src` on a live iframe reloads the viewer and destroys everything the user has in it —
      // selection, scroll position, filters, a half-finished wizard — and a version bump is never
      // worth that. It also does not need to be instant: the fresh version is already in the shared
      // manifest cache, so the *next* tab the user opens constructs with it on its first paint.
      // Please do not "simplify" this guard away into an unconditional setState.
      if (!resolved || (state.manifestVersion ?? props.controllerInfo?.viewerVersion) !== undefined) {
        return null
      }
      return {manifestVersion: resolved, awaitingManifest: false}
    })
  }

  requestWriteMode = (_event: React.MouseEvent) => {
    this.context.requestMode(this.key, ViewerMode.WRITE)
  }

  render() {
    // Determine the source URL with fallbacks
    let srcUrl = this.props.viewerURL
    let viewerVersionMissing = false

    if (!srcUrl && this.props.controllerInfo?.viewerAppName) {
      const baseUrl = process.env.REACT_APP_S3_VIEWERS_URL
      if (baseUrl) {
        const app = this.props.controllerInfo.viewerAppName
        // Manifest first, config second — see componentDidMount.
        const ver = this.renderedVersion()
        if (ver) {
          srcUrl = `${baseUrl}/${app}/${ver}`
        } else {
          // ${baseUrl}/${app} without a version is not a valid viewer path — show in-app error
          viewerVersionMissing = true
        }
      }
    }
    if (!viewerVersionMissing && !srcUrl) {
      // Fallback based on object type
      if (this.props.objectInfo.type === 'Workflow' || this.props.objectInfo.type === 'workflow') {
        srcUrl = '/cwl-viewer/index.html'
      } else if (
        this.props.objectInfo.type === 'Pathway' ||
        this.props.objectInfo.type === 'pathway'
      ) {
        srcUrl = '/pathways-viewer/build/index.html'
      } else if (
        this.props.objectInfo.type === 'Chart' ||
        this.props.objectInfo.type === 'echart'
      ) {
        srcUrl = '/echarts/index.html'
      }
    }

    const containerStyle = {
      flex: 1,
      display: 'flex',
      flexDirection: 'column' as const,
      overflow: 'hidden',
    }

    if (this.state.awaitingManifest) {
      // Blank for one manifest round trip rather than an iframe on a version about to be superseded.
      return <div className={styles.container} style={containerStyle} />
    }

    // Guard: if no viewer could be resolved (empty viewerURL + no controllerInfo.viewerAppName and
    // no type fallback matched), show the in-app error instead of a silent blank iframe. This is the
    // symptom of a result type whose backend web controller (IWebB2GObjectControllerCreator) has not
    // been registered yet.
    if (viewerVersionMissing || !srcUrl) {
      if (!viewerVersionMissing) {
        console.error(
          'ExternalViewer: no viewer could be resolved for object type',
          this.props.objectInfo?.type,
          '- viewerURL and controllerInfo.viewerAppName were both empty',
        )
      }
      return (
        <div className={styles.container} style={containerStyle}>
          <div
            className={styles.viewerErrorPanel}
            role='alert'
            aria-live='polite'
          >
            <h4 className={styles.viewerErrorTitle}>Viewer unavailable</h4>
            <p className={styles.viewerErrorText}>
              {viewerVersionMissing
                ? 'File metadata did not include a viewer version, so the external viewer cannot be loaded. Try re-opening the file after a refresh, or contact support if this persists.'
                : `No viewer is registered for this result type${
                    this.props.objectInfo?.type ? ` (${this.props.objectInfo.type})` : ''
                  }. This viewer may not be available in the web app yet. Please contact support if you expected a viewer here.`}
            </p>
          </div>
        </div>
      )
    }

    return (
      <div
        className={styles.container}
        style={containerStyle}
      >
        <iframe
          title='iframe-container'
          ref={this.iframeRef}
          className={styles.iframe}
          src={srcUrl}
          width='100%'
          style={{flex: 1, border: 'none', minHeight: 0}}
          onLoad={() => {}}
          onError={(e) =>
            console.error('ExternalViewer iframe failed to load:', this.key, srcUrl, e)
          }
        ></iframe>
      </div>
    )
  }
}
